import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { format, subDays, addDays } from "date-fns";
import { 
  Trash,
  Plus,
  CaretLeft,
  CaretRight,
  Warehouse,
  Package,
  CookingPot,
  Calendar,
  CalendarBlank,
  CurrencyDollar,
  MagnifyingGlass,
  X
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";

// Parse unit string to extract conversion info
const parseUnitString = (unitStr) => {
  if (!unitStr) return { primaryUnit: 'CS', secondaryUnit: 'EA', conversion: 1 };
  
  const str = unitStr.toUpperCase();
  
  // Pattern: "1 CS = X LB"
  let match = str.match(/1\s*(CS|CASE)\s*=\s*([\d.]+)\s*(LB|LBS)/i);
  if (match) {
    return { primaryUnit: 'CS', secondaryUnit: 'LB', conversion: parseFloat(match[2]) };
  }
  
  // Pattern: "1 CS = X EA"
  match = str.match(/1\s*(CS|CASE)\s*=\s*([\d.]+)\s*(EA|BAG|BAGS|PC|PCS)/i);
  if (match) {
    return { primaryUnit: 'CS', secondaryUnit: 'EA', conversion: parseFloat(match[2]) };
  }
  
  // Pattern: "1 CS = X OZ"
  match = str.match(/1\s*(CS|CASE)\s*=\s*([\d.]+)\s*(OZ)/i);
  if (match) {
    return { primaryUnit: 'CS', secondaryUnit: 'OZ', conversion: parseFloat(match[2]) };
  }
  
  // Default: EA only
  if (str.includes('EA') || str.includes('EACH')) {
    return { primaryUnit: 'EA', secondaryUnit: null, conversion: 1 };
  }
  
  return { primaryUnit: 'CS', secondaryUnit: 'EA', conversion: 1 };
};

export default function WasteLog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [prepItems, setPrepItems] = useState([]);
  const [wasteLogs, setWasteLogs] = useState([]);
  const [dailySummary, setDailySummary] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    itemType: "product",
    itemId: "",
    quantityCs: "",
    quantityEa: "",
    quantityLb: "",
    quantityOz: "",
    quantityPans: "",  // For pan-based counting
    reason: ""
  });
  
  // Search state for item selection
  const [itemSearch, setItemSearch] = useState("");
  const [logSearch, setLogSearch] = useState("");

  const filteredWasteLogs = useMemo(() => {
    if (!logSearch) return wasteLogs;
    const q = logSearch.toLowerCase();
    return wasteLogs.filter(log => 
      log.item_name?.toLowerCase().includes(q) || log.reason?.toLowerCase().includes(q)
    );
  }, [wasteLogs, logSearch]);

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchProducts();
      fetchPrepItems();
      fetchWasteLogs();
      fetchDailySummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, currentDate]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`, { withCredentials: true });
      setLocations(response.data);
      if (response.data.length > 0) {
        setSelectedLocation(response.data[0].location_id);
      }
    } catch (error) {
      toast.error("Failed to load locations");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?location_id=${selectedLocation}`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to load products");
    }
  };

  const fetchPrepItems = async () => {
    try {
      const response = await axios.get(`${API}/prep-items`, { withCredentials: true });
      setPrepItems(response.data);
    } catch (error) {
      console.error("Failed to load prep items");
    }
  };

  const fetchWasteLogs = async () => {
    try {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API}/waste-logs?location_id=${selectedLocation}&start_date=${dateStr}&end_date=${dateStr}`,
        { withCredentials: true }
      );
      setWasteLogs(response.data);
    } catch (error) {
      console.error("Failed to load waste logs");
    }
  };

  const fetchDailySummary = async () => {
    try {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API}/waste-logs/daily-summary?location_id=${selectedLocation}&date=${dateStr}`,
        { withCredentials: true }
      );
      setDailySummary(response.data);
    } catch (error) {
      console.error("Failed to load daily summary");
    }
  };

  const handleSubmit = async () => {
    if (!formData.itemId) {
      toast.error("Please select an item");
      return;
    }
    
    const hasQuantity = parseFloat(formData.quantityCs) > 0 || 
                        parseFloat(formData.quantityEa) > 0 || 
                        parseFloat(formData.quantityLb) > 0 ||
                        parseFloat(formData.quantityOz) > 0 ||
                        parseFloat(formData.quantityPans) > 0;
    
    if (!hasQuantity) {
      toast.error("Please enter a quantity");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/waste-logs`, {
        item_type: formData.itemType,
        item_id: formData.itemId,
        location_id: selectedLocation,
        quantity_cs: parseFloat(formData.quantityCs) || 0,
        quantity_ea: parseFloat(formData.quantityEa) || 0,
        quantity_lb: parseFloat(formData.quantityLb) || 0,
        quantity_oz: parseFloat(formData.quantityOz) || 0,
        quantity_pans: parseFloat(formData.quantityPans) || 0,
        reason: formData.reason,
        recorded_at: format(currentDate, 'yyyy-MM-dd')  // Send the selected date for backdating
      }, { withCredentials: true });
      
      // Check if response indicates oz_conversion is needed
      if (response.data?.error === "oz_conversion_required") {
        toast.error(
          `${response.data.message}\n\nPlease update the ${formData.itemType === 'prep' ? 'Prep Item' : 'Product'} with oz conversion settings.`,
          { duration: 6000 }
        );
        return;
      }
      
      const isToday = format(currentDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
      toast.success(isToday ? "Waste logged successfully" : `Waste logged for ${format(currentDate, 'MMM d, yyyy')}`);
      setDialogOpen(false);
      resetForm();
      fetchWasteLogs();
      fetchDailySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log waste");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (wasteId) => {
    if (!window.confirm("Delete this waste entry?")) return;
    
    try {
      await axios.delete(`${API}/waste-logs/${wasteId}`, { withCredentials: true });
      toast.success("Entry deleted");
      fetchWasteLogs();
      fetchDailySummary();
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };

  const resetForm = () => {
    setFormData({
      itemType: "product",
      itemId: "",
      quantityCs: "",
      quantityEa: "",
      quantityLb: "",
      quantityOz: "",
      quantityPans: "",
      reason: ""
    });
    setItemSearch("");
  };

  const getFilteredProducts = () => {
    // Only show products that have allow_in_waste_log enabled
    const wasteLogProducts = products.filter(p => p.allow_in_waste_log === true);
    if (!itemSearch) return wasteLogProducts;
    return wasteLogProducts.filter(p => 
      p.name.toLowerCase().includes(itemSearch.toLowerCase())
    );
  };

  const getFilteredPrepItems = () => {
    // Only show prep items that have allow_in_waste_log enabled
    const wasteLogPrepItems = prepItems.filter(p => p.allow_in_waste_log === true);
    if (!itemSearch) return wasteLogPrepItems;
    return wasteLogPrepItems.filter(p => 
      p.name.toLowerCase().includes(itemSearch.toLowerCase())
    );
  };

  const getSelectedItem = () => {
    if (formData.itemType === "product") {
      return products.find(p => p.product_id === formData.itemId);
    }
    return prepItems.find(p => p.prep_id === formData.itemId);
  };

  const getUnitInfo = () => {
    const item = getSelectedItem();
    if (formData.itemType === "prep") {
      return { primaryUnit: 'EA', secondaryUnit: null, conversion: 1 };
    }
    return parseUnitString(item?.unit);
  };

  const isToday = format(currentDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="waste-log-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl tracking-wide font-semibold">
            WASTE LOG
          </h1>
          <p className="text-muted-foreground mt-1">
            Track wasted products and prep items daily
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-primary" data-testid="add-waste-btn">
          <Plus size={18} className="mr-2" />
          Log Waste
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 max-w-xs">
          <Label className="text-xs text-muted-foreground mb-1 block">Location</Label>
          <Select value={selectedLocation || ""} onValueChange={setSelectedLocation}>
            <SelectTrigger data-testid="location-select">
              <Warehouse size={16} className="mr-2" />
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map(loc => (
                <SelectItem key={loc.location_id} value={loc.location_id}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Date Navigation - Daily */}
        <div className="flex items-end gap-2">
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setCurrentDate(subDays(currentDate, 1))}
            data-testid="prev-day-btn"
          >
            <CaretLeft size={18} />
          </Button>
          
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className="min-w-[180px] justify-start"
                data-testid="date-picker-btn"
              >
                <Calendar size={16} className="mr-2" />
                <span className="font-medium">
                  {isToday ? "Today" : format(currentDate, 'MMM d, yyyy')}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="single"
                selected={currentDate}
                onSelect={(date) => {
                  if (date) {
                    setCurrentDate(date);
                    setCalendarOpen(false);
                  }
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setCurrentDate(addDays(currentDate, 1))}
            data-testid="next-day-btn"
          >
            <CaretRight size={18} />
          </Button>
          
          {!isToday && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={goToToday}
              className="text-primary"
              data-testid="today-btn"
            >
              Today
            </Button>
          )}
        </div>
      </div>

      {/* Daily Summary */}
      {dailySummary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border rounded-xl bg-red-50/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <CurrencyDollar size={24} weight="duotone" className="text-red-600" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">Total Waste Value</p>
                  <p className="text-2xl font-semibold tabular-nums text-red-600">
                    ${dailySummary.total_value?.toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border rounded-xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                  <Package size={24} weight="duotone" className="text-muted-foreground" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">Total Entries</p>
                  <p className="text-2xl font-semibold tabular-nums">{dailySummary.total_entries}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border rounded-xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                  <Trash size={24} weight="duotone" className="text-muted-foreground" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">Items with Waste</p>
                  <p className="text-2xl font-semibold tabular-nums">{dailySummary.by_item?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="entries" className="w-full">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="entries" className="font-heading tracking-wide">
            ALL ENTRIES
          </TabsTrigger>
          <TabsTrigger value="summary" className="font-heading tracking-wide">
            BY ITEM
          </TabsTrigger>
        </TabsList>

        {/* Entries Tab */}
        <TabsContent value="entries" className="mt-4">
          <Card className="border rounded-xl">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg tracking-wide">
                WASTE ENTRIES - {format(currentDate, 'MMMM d, yyyy')}
              </CardTitle>
              {wasteLogs.length > 0 && (
                <div className="relative mt-2">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    data-testid="waste-log-search"
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                    placeholder="Search by item name or reason..."
                    className="pl-10 pr-10"
                  />
                  {logSearch && (
                    <button onClick={() => setLogSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      <X size={18} />
                    </button>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {wasteLogs.length > 0 ? (
                <div className="space-y-2">
                  {filteredWasteLogs.map(log => (
                    <div 
                      key={log.waste_id}
                      data-testid={`waste-entry-${log.waste_id}`}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {log.item_type === "product" ? (
                          <Package size={20} className="text-primary" />
                        ) : (
                          <CookingPot size={20} className="text-secondary" />
                        )}
                        <div>
                          <p className="font-medium">{log.item_name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{log.total_quantity?.toFixed(2)} {log.unit}</span>
                            {log.reason && <span>• {log.reason}</span>}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(log.recorded_at), 'h:mm a')}
                            {log.price_snapshot && (
                              <span className="ml-2 text-muted-foreground/70">
                                @ ${log.price_snapshot.toFixed(2)}/{log.unit}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-red-600">${log.value?.toFixed(2)}</span>
                        {isAdmin && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDelete(log.waste_id)}
                          >
                            <Trash size={16} className="text-destructive" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No waste entries for {isToday ? "today" : format(currentDate, 'MMMM d, yyyy')}.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Summary Tab */}
        <TabsContent value="summary" className="mt-4">
          <Card className="border rounded-xl">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg tracking-wide">
                WASTE BY ITEM - {format(currentDate, 'MMMM d, yyyy')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dailySummary?.by_item?.length > 0 ? (
                <div className="space-y-2">
                  {dailySummary.by_item.map(item => (
                    <div 
                      key={item.item_id}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {item.item_type === "product" ? (
                          <Package size={20} className="text-primary" />
                        ) : (
                          <CookingPot size={20} className="text-secondary" />
                        )}
                        <div>
                          <p className="font-medium">{item.item_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.total_quantity?.toFixed(2)} {item.unit} • {item.entries} {item.entries === 1 ? 'entry' : 'entries'}
                          </p>
                        </div>
                      </div>
                      <span className="font-semibold text-red-600">${item.total_value?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No waste data for {isToday ? "today" : format(currentDate, 'MMMM d, yyyy')}.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Waste Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-wide">LOG WASTE</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Date Indicator for Backdating */}
            {format(currentDate, 'yyyy-MM-dd') !== format(new Date(), 'yyyy-MM-dd') && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                <CalendarBlank size={18} />
                <span className="text-sm font-medium">
                  Recording for: {format(currentDate, 'MMMM d, yyyy')}
                </span>
              </div>
            )}
            
            {/* Item Type */}
            <div>
              <Label>Item Type</Label>
              <Select 
                value={formData.itemType} 
                onValueChange={(val) => setFormData(p => ({ ...p, itemType: val, itemId: "" }))}
              >
                <SelectTrigger data-testid="item-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">
                    <div className="flex items-center gap-2">
                      <Package size={16} />
                      Product
                    </div>
                  </SelectItem>
                  <SelectItem value="prep">
                    <div className="flex items-center gap-2">
                      <CookingPot size={16} />
                      Prep Item
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Item Selection with Search */}
            <div>
              <Label>{formData.itemType === "product" ? "Product" : "Prep Item"}</Label>
              <div className="space-y-2">
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input 
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    placeholder={`Search ${formData.itemType === "product" ? "products" : "prep items"}...`}
                    className="pl-9"
                    data-testid="item-search-input"
                  />
                </div>
                <Select 
                  value={formData.itemId} 
                  onValueChange={(val) => setFormData(p => ({ ...p, itemId: val }))}
                >
                  <SelectTrigger data-testid="item-select">
                    <SelectValue placeholder={`Select ${formData.itemType}`} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {formData.itemType === "product" ? (
                      getFilteredProducts().length > 0 ? (
                        getFilteredProducts().map(p => (
                          <SelectItem key={p.product_id} value={p.product_id}>
                            {p.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-3 text-center text-sm text-muted-foreground">
                          <p>No products enabled for waste log</p>
                          <p className="text-xs mt-1">Enable "Allow in Waste Log" in Product settings</p>
                        </div>
                      )
                    ) : (
                      getFilteredPrepItems().length > 0 ? (
                        getFilteredPrepItems().map(p => (
                          <SelectItem key={p.prep_id} value={p.prep_id}>
                            {p.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-3 text-center text-sm text-muted-foreground">
                          <p>No prep items enabled for waste log</p>
                          <p className="text-xs mt-1">Enable "Allow in Waste Log" in Prep Calculator</p>
                        </div>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Unit Info Display */}
            {formData.itemId && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  {formData.itemType === "product" 
                    ? `Unit: ${getSelectedItem()?.unit || 'N/A'}`
                    : `Counted in pans (EA)`
                  }
                </p>
                {getSelectedItem()?.price > 0 && (
                  <p className="text-sm text-muted-foreground">
                    Price: ${getSelectedItem()?.price?.toFixed(2)}/{getUnitInfo().primaryUnit}
                  </p>
                )}
                {getSelectedItem()?.oz_conversion ? (
                  <p className="text-sm text-green-600">
                    Oz Conversion: {getSelectedItem()?.oz_conversion} oz per {getUnitInfo().primaryUnit} (oz logging enabled)
                  </p>
                ) : (
                  formData.itemType === "product" && !getSelectedItem()?.unit?.toUpperCase().includes('LB') && (
                    <p className="text-sm text-orange-600">
                      No oz conversion defined - oz logging will require admin to update product
                    </p>
                  )
                )}
                {getSelectedItem()?.unit?.toUpperCase().includes('LB') && (
                  <p className="text-sm text-green-600">
                    LB unit detected - using 16 oz = 1 LB for conversion
                  </p>
                )}
                {getSelectedItem()?.pans_to_oz && (
                  <p className="text-sm text-green-600">
                    Pans to Oz: {getSelectedItem()?.pans_to_oz} oz per pan (can log by pans + oz)
                  </p>
                )}
              </div>
            )}

            {/* Quantity Inputs - Based on item type and unit */}
            {formData.itemId && (
              <div className="space-y-3">
                <Label>Quantity Wasted</Label>
                
                {formData.itemType === "prep" ? (
                  <div className="space-y-2">
                    {/* Pans input */}
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={formData.quantityPans}
                        onChange={(e) => setFormData(p => ({ ...p, quantityPans: e.target.value }))}
                        placeholder="0"
                        className="text-center"
                        min="0"
                        step="0.5"
                        data-testid="quantity-pans-input"
                      />
                      <span className="text-sm text-muted-foreground w-16">pans</span>
                    </div>
                    {/* Oz input - always show for prep items with pans_to_oz */}
                    {getSelectedItem()?.pans_to_oz && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={formData.quantityOz}
                          onChange={(e) => setFormData(p => ({ ...p, quantityOz: e.target.value }))}
                          placeholder="0"
                          className="text-center"
                          min="0"
                          step="1"
                          data-testid="quantity-oz-input"
                        />
                        <span className="text-sm text-muted-foreground w-16">OZ</span>
                      </div>
                    )}
                    {getSelectedItem()?.pans_to_oz && (
                      <p className="text-xs text-muted-foreground">
                        Both pans and oz will be combined ({getSelectedItem()?.pans_to_oz} oz/pan)
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Primary Unit (CS) */}
                    {getUnitInfo().primaryUnit === 'CS' && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={formData.quantityCs}
                          onChange={(e) => setFormData(p => ({ ...p, quantityCs: e.target.value }))}
                          placeholder="0"
                          className="text-center"
                          min="0"
                          step="0.5"
                          data-testid="quantity-cs-input"
                        />
                        <span className="text-sm text-muted-foreground w-16">CS</span>
                      </div>
                    )}
                    
                    {/* Secondary Unit based on conversion */}
                    {getUnitInfo().secondaryUnit === 'EA' && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={formData.quantityEa}
                          onChange={(e) => setFormData(p => ({ ...p, quantityEa: e.target.value }))}
                          placeholder="0"
                          className="text-center"
                          min="0"
                          step="1"
                          data-testid="quantity-ea-input"
                        />
                        <span className="text-sm text-muted-foreground w-16">EA</span>
                      </div>
                    )}
                    
                    {getUnitInfo().secondaryUnit === 'LB' && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={formData.quantityLb}
                          onChange={(e) => setFormData(p => ({ ...p, quantityLb: e.target.value }))}
                          placeholder="0"
                          className="text-center"
                          min="0"
                          step="0.25"
                          data-testid="quantity-lb-input"
                        />
                        <span className="text-sm text-muted-foreground w-16">LB</span>
                      </div>
                    )}
                    
                    {getUnitInfo().secondaryUnit === 'OZ' && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={formData.quantityOz}
                          onChange={(e) => setFormData(p => ({ ...p, quantityOz: e.target.value }))}
                          placeholder="0"
                          className="text-center"
                          min="0"
                          step="1"
                          data-testid="quantity-oz-input"
                        />
                        <span className="text-sm text-muted-foreground w-16">OZ</span>
                      </div>
                    )}
                    
                    {/* Always show OZ input for items with oz_conversion */}
                    {getSelectedItem()?.oz_conversion && getUnitInfo().secondaryUnit !== 'OZ' && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={formData.quantityOz}
                          onChange={(e) => setFormData(p => ({ ...p, quantityOz: e.target.value }))}
                          placeholder="0"
                          className="text-center"
                          min="0"
                          step="1"
                          data-testid="quantity-oz-input"
                        />
                        <span className="text-sm text-muted-foreground w-16">OZ</span>
                      </div>
                    )}
                    
                    {/* Pans input for products with pans_to_oz */}
                    {getSelectedItem()?.pans_to_oz && (
                      <>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={formData.quantityPans}
                            onChange={(e) => setFormData(p => ({ ...p, quantityPans: e.target.value }))}
                            placeholder="0"
                            className="text-center"
                            min="0"
                            step="0.5"
                            data-testid="quantity-pans-input"
                          />
                          <span className="text-sm text-muted-foreground w-16">pans</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Pans + oz will be combined ({getSelectedItem()?.pans_to_oz} oz/pan)
                        </p>
                      </>
                    )}
                    
                    {/* EA as primary for items without CS */}
                    {getUnitInfo().primaryUnit === 'EA' && !getUnitInfo().secondaryUnit && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={formData.quantityEa}
                          onChange={(e) => setFormData(p => ({ ...p, quantityEa: e.target.value }))}
                          placeholder="0"
                          className="text-center"
                          min="0"
                          step="1"
                          data-testid="quantity-ea-input"
                        />
                        <span className="text-sm text-muted-foreground w-16">EA</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Reason */}
            <div>
              <Label>Reason (Optional)</Label>
              <Input 
                value={formData.reason}
                onChange={(e) => setFormData(p => ({ ...p, reason: e.target.value }))}
                placeholder="e.g., Expired, Dropped, Overcooked"
                data-testid="reason-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="submit-waste-btn"
            >
              <Trash size={16} className="mr-2" />
              {saving ? "Saving..." : "Log Waste"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
